import { ModalGeneric } from "@/components/utils/ModalGeneric";

export type PreviewServico = {
  items: Array<{ descricao: string; quantidade: number; valor: number }>;
  total: number;
  natureza_operacao: string;
  item_lista_servico: string;
  codigo_cnae: string;
  iss_retido: boolean;
  valor_ir?: string;
  valor_pis?: string;
  valor_cofins?: string;
  valor_csll?: string;
};

export type PreviewData = {
  emitente: {
    nome: string;
    cnpj: string;
    inscricao_municipal?: string;
    inscricao_estadual?: string;
    endereco?: string;
    numero?: string;
    bairro?: string;
    municipio?: string;
    uf?: string;
    regime?: string;
  };
  tomador: {
    nome: string;
    doc: string;
    email?: string;
    telefone?: string;
    endereco?: string;
    municipio?: string;
    uf?: string;
    cep?: string;
  };
  servico: PreviewServico;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  data: PreviewData | null;
};

function formatCurrency(value?: string | number) {
  return Number(value ?? 0).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });
}

export default function ModalPreviaNotaFiscal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  data,
}: Props) {
  if (!isOpen || !data) return null;

  const totalFormatado = formatCurrency(data.servico.total);

  return (
    <ModalGeneric
      onClose={onClose}
      size="max-w-3xl w-full max-h-[90vh] overflow-hidden flex-1 overflow-y-auto space-y-5 pr-2 scrollbar-thin"
    >
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-cinza-800">
              Prévia da Nota Fiscal
            </h2>
            <p className="text-sm text-cinza-500">
              Confira os dados antes de emitir
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-cinza-500">Valor total</p>
            <p className="text-lg font-bold text-cinza-800">{totalFormatado}</p>
          </div>
        </div>

        {/* Emitente + Tomador */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-cinza-100 p-4 space-y-2">
            <p className="text-sm font-semibold text-cinza-800">Emitente</p>
            <div className="text-sm text-cinza-700 space-y-1">
              <p className="font-medium">{data.emitente.nome}</p>
              <p>CNPJ: {data.emitente.cnpj}</p>
              {data.emitente.endereco && (
                <p>
                  {data.emitente.endereco}
                  {data.emitente.numero ? `, ${data.emitente.numero}` : ""}
                  {data.emitente.bairro ? ` - ${data.emitente.bairro}` : ""}
                </p>
              )}
              {data.emitente.municipio && (
                <p>
                  {data.emitente.municipio} / {data.emitente.uf}
                </p>
              )}
              {data.emitente.inscricao_municipal && (
                <p>
                  IM: {data.emitente.inscricao_municipal}
                  {data.emitente.inscricao_estadual
                    ? ` • IE: ${data.emitente.inscricao_estadual}`
                    : ""}
                </p>
              )}
              {data.emitente.regime && (
                <p className="text-cinza-500">Regime: {data.emitente.regime}</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-cinza-100 p-4 space-y-2">
            <p className="text-sm font-semibold text-cinza-800">Tomador</p>
            <div className="text-sm text-cinza-700 space-y-1">
              <p className="font-medium">{data.tomador.nome}</p>
              <p>{data.tomador.doc}</p>
              {data.tomador.email && <p>E-mail: {data.tomador.email}</p>}
              {data.tomador.telefone && (
                <p>Telefone: {data.tomador.telefone}</p>
              )}
              {data.tomador.endereco && (
                <p>Endereço: {data.tomador.endereco}</p>
              )}
              {data.tomador.municipio && (
                <p>
                  {data.tomador.municipio}
                  {data.tomador.uf ? ` / ${data.tomador.uf}` : ""}
                  {data.tomador.cep ? ` — CEP: ${data.tomador.cep}` : ""}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Itens do Serviço */}
        <div className="bg-white rounded-2xl border border-cinza-100 p-4 space-y-3">
          <p className="text-sm font-semibold text-cinza-800">
            Itens do Serviço
          </p>
          {data.servico.items.length > 0 ? (
            <div className="space-y-1">
              {data.servico.items.map((item, i) => (
                <div
                  key={i}
                  className="flex justify-between items-start text-sm text-cinza-700 py-2 border-b border-cinza-50 last:border-0"
                >
                  <span className="flex-1 pr-4">
                    {item.descricao || "-"}
                    {item.quantidade > 1 && (
                      <span className="text-cinza-400 ml-1">
                        ×{item.quantidade}
                      </span>
                    )}
                  </span>
                  <span className="whitespace-nowrap font-medium">
                    {formatCurrency(item.valor)}
                  </span>
                </div>
              ))}
              <div className="flex justify-between text-sm font-bold text-cinza-800 pt-2">
                <span>Total</span>
                <span>{totalFormatado}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-cinza-400">Nenhum item na proposta</p>
          )}
        </div>

        {/* Informações de Emissão */}
        <div className="bg-white rounded-2xl border border-cinza-100 p-4 space-y-3">
          <p className="text-sm font-semibold text-cinza-800">
            Informações de Emissão
          </p>
          <div className="grid grid-cols-3 gap-3 text-sm text-cinza-700">
            <div>
              <p className="text-cinza-500">Natureza da Operação</p>
              <p className="font-medium">
                {data.servico.natureza_operacao || "-"}
              </p>
            </div>
            <div>
              <p className="text-cinza-500">Item Lista de Serviço</p>
              <p className="font-medium">
                {data.servico.item_lista_servico || "-"}
              </p>
            </div>
            <div>
              <p className="text-cinza-500">CNAE</p>
              <p className="font-medium">{data.servico.codigo_cnae || "-"}</p>
            </div>
          </div>
        </div>

        {/* ISS Retido */}
        {data.servico.iss_retido && (
          <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4 space-y-3">
            <p className="text-sm font-semibold text-amber-800">
              Retenções (ISS Retido)
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm text-cinza-700">
              <div>
                <p className="text-cinza-500">Imposto de Renda (IR)</p>
                <p className="font-medium">
                  {formatCurrency(data.servico.valor_ir)}
                </p>
              </div>
              <div>
                <p className="text-cinza-500">PIS</p>
                <p className="font-medium">
                  {formatCurrency(data.servico.valor_pis)}
                </p>
              </div>
              <div>
                <p className="text-cinza-500">COFINS</p>
                <p className="font-medium">
                  {formatCurrency(data.servico.valor_cofins)}
                </p>
              </div>
              <div>
                <p className="text-cinza-500">CSLL</p>
                <p className="font-medium">
                  {formatCurrency(data.servico.valor_csll)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Ações */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-6 py-2 rounded-3xl border border-cinza-300 text-cinza-700 text-sm font-semibold hover:bg-cinza-50 disabled:opacity-50"
          >
            Voltar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-8 py-2 rounded-3xl text-white text-sm font-semibold ${
              isLoading ? "bg-gray-400 cursor-not-allowed" : "bg-azul-400"
            }`}
          >
            {isLoading ? "Emitindo..." : "Emitir Nota"}
          </button>
        </div>
      </div>
    </ModalGeneric>
  );
}
